import React, { useState, useEffect } from 'react';
import { Sparkles, X, RefreshCw, Check } from 'lucide-react';
import apiService from '../../services/api';
import BaseModal from '../common/BaseModal';

const AiCategorizeModal = ({
  isOpen,
  onClose,
  onConfirm,
  transaction
}) => {
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [error, setError] = useState(null);

  // Available categories with icons
  const categories = [
    { name: 'grocery', label: 'Grocery', icon: '🛒' },
    { name: 'restaurant', label: 'Restaurant', icon: '🍔' },
    { name: 'gas', label: 'Gas/Fuel', icon: '⛽' },
    { name: 'pharmacy', label: 'Pharmacy', icon: '💊' },
    { name: 'clothing', label: 'Clothing', icon: '👕' },
    { name: 'entertainment', label: 'Entertainment', icon: '🎬' },
    { name: 'transport', label: 'Transport', icon: '🚗' },
    { name: 'health', label: 'Health', icon: '🏥' },
    { name: 'utilities', label: 'Utilities', icon: '💡' },
    { name: 'shopping', label: 'Shopping', icon: '🛍️' },
    { name: 'alcohol', label: 'Alcohol', icon: '🍷' },
    { name: 'transfer', label: 'Transfer', icon: '💸' },
    { name: 'income', label: 'Income', icon: '💵' },
    { name: 'housing', label: 'Housing', icon: '🏠' },
    { name: 'cash', label: 'Cash', icon: '💵' },
    { name: 'other', label: 'Other', icon: '💰' }
  ];

  useEffect(() => {
    if (isOpen && transaction) {
      getAiSuggestion();
    }
  }, [isOpen, transaction]);

  const getAiSuggestion = async () => {
    setLoading(true);
    setError(null);
    setAiSuggestion(null);
    setSelectedCategory(null);
    setSelectedIcon(null);

    try {
      // Start AI categorization job
      const jobResponse = await apiService.categorizeTransactionWithAi(transaction.description);
      const jobId = jobResponse.job_id;

      // Poll for job completion
      const pollInterval = 500; // Poll every 500ms
      const maxAttempts = 20; // Max 10 seconds

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        const statusResponse = await apiService.getTaskStatus(jobId);

        if (statusResponse.status === 'completed' && statusResponse.result) {
          const category = statusResponse.result.category;
          const categoryInfo = categories.find(c => c.name === category) || categories.find(c => c.name === 'other');

          setAiSuggestion({
            category: categoryInfo.name,
            label: categoryInfo.label,
            icon: categoryInfo.icon
          });

          // Pre-select the AI suggestion
          setSelectedCategory(categoryInfo.name);
          setSelectedIcon(categoryInfo.icon);
          break;
        } else if (statusResponse.status === 'failed') {
          throw new Error(statusResponse.error || 'AI categorization failed');
        }
      }
    } catch (err) {
      console.error('Error getting AI suggestion:', err);
      setError(err.message || 'Failed to get AI suggestion');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    const categoryInfo = categories.find(c => c.name === categoryName);
    setSelectedCategory(categoryName);
    setSelectedIcon(categoryInfo.icon);
  };

  const handleConfirm = async () => {
    if (!selectedCategory) return;

    const categoryInfo = categories.find(c => c.name === selectedCategory);
    await onConfirm(selectedCategory, categoryInfo.icon, categoryInfo.label);
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={loading ? () => {} : handleCancel}
      size="lg"
      showCloseButton={false}
      closeOnBackdrop={!loading}
    >
      {/* Custom Header with gradient and X button */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 flex items-center justify-between">
        <div className="flex items-center">
          <Sparkles className="h-6 w-6 text-purple-500 dark:text-purple-400 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">AI Categorization</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Let AI suggest a category for this transaction</p>
          </div>
        </div>
        <button
          onClick={handleCancel}
          disabled={loading}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
          {/* Transaction Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-500 mb-1">Transaction Description</p>
            <p className="font-medium text-gray-900">{transaction?.description}</p>
            {transaction?.total_amount && (
              <p className="text-sm text-gray-600 mt-2">
                Amount: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(transaction.total_amount)}
              </p>
            )}
          </div>

          {/* AI Suggestion */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative">
                <Sparkles className="h-12 w-12 text-purple-500 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 text-purple-600 animate-spin" />
                </div>
              </div>
              <p className="mt-4 text-gray-600 font-medium">Analyzing transaction...</p>
              <p className="text-sm text-gray-500">AI is thinking</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={getAiSuggestion}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Try Again
              </button>
            </div>
          ) : aiSuggestion ? (
            <div>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <Sparkles className="h-5 w-5 text-purple-500 mr-2" />
                  <span className="font-medium text-purple-900">AI Suggestion</span>
                </div>
                <div className="flex items-center">
                  <span className="text-3xl mr-3">{aiSuggestion.icon}</span>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{aiSuggestion.label}</p>
                    <p className="text-sm text-gray-600">Category: {aiSuggestion.category}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accept or choose a different category
                </label>
                <select
                  value={selectedCategory || ''}
                  onChange={handleCategoryChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a category...</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCategory && selectedCategory !== aiSuggestion.category && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    You've selected a different category than the AI suggestion.
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !selectedCategory}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
            >
              <Check className="h-4 w-4 mr-2" />
              Apply Category
            </button>
          </div>
        </div>
    </BaseModal>
  );
};

export default AiCategorizeModal;
